import {
  appFormsTreeAreaNodeMapExtended,
  appFormsTreeFieldOptionsMapExtended,
  AREA_ID_PREFIX,
} from 'app/services/autoLockForms/autoLockForms';
import { normalizeLegacyAppFormsTreeId } from 'app/services/autoLockForms/autoLockForms.utils';
import memoizeOne from 'memoize-one';
import { createSelector } from 'reselect';

import {
  ChecklistExecutionSnapshotTargetType,
  isEntityScopedChecklistTargetType,
  isFinancialRecordChecklistTargetType,
} from 'constants/checklists';

import { selectCompletenessChecklistIdSet } from './completenessChecklists.selectors';
import { selectLoanApplicationChecklistResultsList } from './loanApplicationChecklistResults.selectors';

/**
 * Rolls the field- and section-level "Needed" checklist links UP the loan-editor
 * navigation tree (DEV-4496). It is the NAV-level sibling of
 * `selectIncompleteLinkedFields` (field-keyed, per-form) and
 * `selectIncompleteLinkedSections` (full-area-ref-keyed) — but instead of keying
 * by a single field/area it tallies, for every ANCESTOR nav level
 * (page/section/subsection/tab — and the form area), how many still-needed
 * checklist refs live underneath it. So a nav item (outer rail = `section:…`,
 * inner section nav = `tab:…`/`subsection:…`) can ask "does anything below me
 * still need attention?" without opening each field.
 *
 * A linked `fieldRef` is one of two shapes (exactly as the two sibling selectors
 * discriminate them):
 *  - a FIELD path `${section}-${subsection}-${tab}-${form}-${leaf}` — its ancestor
 *    area ids come from `appFormsTreeFieldOptionsMapExtended[ref].ancestorAreaIds`;
 *  - a SECTION/area ref prefixed `page:`/`section:`/`subsection:`/`tab:` — the
 *    area itself plus its `ancestorIds` from `appFormsTreeAreaNodeMapExtended`.
 * The EXTENDED maps are used so checklist links onto runtime-registered custom
 * loan-editor forms roll up too.
 *
 * Only GENUINELY-needed refs are tallied: an item counts when its `result` is not
 * `true` AND it is not overridden. An overridden item is resolved/suppressed (the
 * field-level indicator shows it as the orange "Overridden" chip, not the purple
 * "Needed" highlight), so it must NOT light a nav row as still-needed.
 *
 * No borrower-dimension keying needed here (DEV-4684, investigated): the nav rollup is
 * area-level, not field/borrower-level — it only answers "does anything below me still need
 * attention", so a Borrower-scoped snapshot's failing item rolls up exactly like a
 * LoanApplication one (and if TWO borrowers each fail the same per-borrower field, both
 * genuinely contribute to `count`, since each is a real still-needed item for its own borrower).
 *
 * @returns selector → `{ [areaId: string]: { needed: true, count: number } }`
 *   keyed by every ancestor area id with ≥1 needed descendant ref.
 */
const isItemOverridden = ({ wasOverriden, overrideHistory }) =>
  !!wasOverriden ||
  !!overrideHistory?.wasOverriden ||
  !!overrideHistory?.entries?.some(entry => entry?.change?.type === 'Override');

const AREA_ID_PREFIXES = Object.values(AREA_ID_PREFIX);

const isAreaRef = ref => AREA_ID_PREFIXES.some(prefix => ref.startsWith(prefix));

// Resolve a needed ref to the set of nav area ids it should light: a field path
// lights its ancestor areas; an area ref lights itself plus its ancestors.
const resolveNeededAreaIds = ref => {
  if (isAreaRef(ref)) {
    const node = appFormsTreeAreaNodeMapExtended[ref];

    return node ? [...node.ancestorIds, node.id] : [ref];
  }

  return appFormsTreeFieldOptionsMapExtended[ref]?.ancestorAreaIds ?? [];
};

// Tally every genuinely-needed ref in a snapshot's items into `map` (keyed by nav
// area id): skip evaluated-true and overridden items, then bump the count on each
// ancestor area the ref lights. Shared by the loan-wide roll-up and the
// per-borrower one so both discriminate needed refs identically.
const accumulateNeededAreaIds = (map, items) => {
  items.forEach(item => {
    const { result, fieldRef } = item;

    if (!fieldRef || result === true || isItemOverridden(item)) {
      return;
    }

    resolveNeededAreaIds(normalizeLegacyAppFormsTreeId(fieldRef)).forEach(areaId => {
      map[areaId] = { needed: true, count: (map[areaId]?.count ?? 0) + 1 };
    });
  });
};

export const selectIncompleteLinkedNav = memoizeOne(() =>
  createSelector(
    selectLoanApplicationChecklistResultsList,
    selectCompletenessChecklistIdSet,
    (checklistResultsList, completenessChecklistIdSet) => {
      const map = {};

      checklistResultsList.forEach(snapshot => {
        const checklistId = snapshot.target?.checklistId;
        const items = snapshot.status?.result?.items;

        // Only the company's completeness checklist(s) roll up as "Needed" on the nav (DEV-4603).
        if (!checklistId || !items || !completenessChecklistIdSet.has(checklistId)) {
          return;
        }

        accumulateNeededAreaIds(map, items);
      });

      return map;
    },
  ),
);

/**
 * The LOAN-scoped half of the borrower-drawer roll-up (DEV-5145). A completeness checklist may
 * author a borrower-field or borrower-area `fieldRef` on the LoanApplication-target checklist
 * rather than on a hidden Borrower-target companion — most companies do, since the designation
 * slot holds a single loan-level checklist. Such an item has no owning borrower, so it belongs to
 * EVERY borrower's drawer: it is what lights the outer rail, and without it the drawer's nav and
 * the borrowers-list badge stay blank while the rail says "Needed".
 *
 * Deliberately the complement of `selectIncompleteLinkedNavByBorrower` (an entity-scoped
 * targetType — Borrower/Reo/Income/Asset/Liability), not `selectIncompleteLinkedNav` — the
 * loan-wide map also folds in entity-scoped items, so merging THAT into each borrower would
 * re-stamp a co-borrower's (or a sibling financial-info row's) gap on the primary, the exact
 * regression DEV-4684 fixed and DEV-5801 extended to every per-row entity type.
 *
 * @returns selector → `{ [areaId: string]: { needed: true, count: number } }`
 */
export const selectIncompleteLinkedNavLoanScoped = memoizeOne(() =>
  createSelector(
    selectLoanApplicationChecklistResultsList,
    selectCompletenessChecklistIdSet,
    (checklistResultsList, completenessChecklistIdSet) => {
      const map = {};

      checklistResultsList.forEach(snapshot => {
        const checklistId = snapshot.target?.checklistId;
        const items = snapshot.status?.result?.items;

        if (
          isEntityScopedChecklistTargetType(snapshot.target?.targetType) ||
          !checklistId ||
          !items ||
          !completenessChecklistIdSet.has(checklistId)
        ) {
          return;
        }

        accumulateNeededAreaIds(map, items);
      });

      return map;
    },
  ),
);

/**
 * The per-BORROWER sibling of `selectIncompleteLinkedNav` (DEV-4810). The loan-wide
 * roll-up above intentionally is NOT borrower-dimensioned, which is right for the
 * outer rail ("does anything below this nav item need attention"). But the borrower
 * edit drawer shows ONE borrower at a time, so its per-tab / per-section indicators
 * must reflect only the borrower on screen — a co-borrower's gap must not light the
 * primary's tab or section. This partitions the SAME area-id roll-up by the
 * snapshot's `Borrower` targetId.
 *
 * Only Borrower-scoped completeness snapshots contribute. A LoanApplication snapshot is loan-wide
 * and has no single owning borrower, so its items are surfaced separately by
 * `selectIncompleteLinkedNavLoanScoped` and merged in per consumer via `mergeNavNeededMaps`.
 * Overridden / evaluated-true items are excluded identically via the shared
 * `accumulateNeededAreaIds`.
 *
 * @returns selector → `{ [borrowerId]: { [areaId]: { needed: true, count: number } } }`
 */
export const selectIncompleteLinkedNavByBorrower = memoizeOne(() =>
  createSelector(
    selectLoanApplicationChecklistResultsList,
    selectCompletenessChecklistIdSet,
    (checklistResultsList, completenessChecklistIdSet) => {
      const byBorrower = {};

      checklistResultsList.forEach(snapshot => {
        const checklistId = snapshot.target?.checklistId;
        const targetId = snapshot.target?.targetId;
        const items = snapshot.status?.result?.items;

        if (
          snapshot.target?.targetType !== ChecklistExecutionSnapshotTargetType.BORROWER ||
          !targetId ||
          !checklistId ||
          !items ||
          !completenessChecklistIdSet.has(checklistId)
        ) {
          return;
        }

        // Accumulate into this borrower's existing map (a borrower can span >1 snapshot) or a fresh
        // one, and only keep the key once something actually rolled up — no empty entries for a
        // borrower whose fields are all complete/overridden.
        const map = byBorrower[targetId] ?? {};
        accumulateNeededAreaIds(map, items);

        if (Object.keys(map).length) {
          byBorrower[targetId] = map;
        }
      });

      return byBorrower;
    },
  ),
);

/**
 * The per-FINANCIAL-INFO-RECORD sibling of `selectIncompleteLinkedNavByBorrower` (HZ-3605).
 * The financial-info grids list one row per REO / income / asset / liability record, and each
 * row needs its OWN count so an LO can see which record to open — a sibling row's gap must
 * never light this row, exactly as a co-borrower's must not light the primary's.
 *
 * Keyed flat by the snapshot's `targetId` (the record id) rather than nested per target type:
 * record ids are GUIDs, so one namespace is unambiguous, and each grid narrows by its own
 * subsection area ids anyway. `Borrower` is entity-scoped too but owns a separate surface, so
 * `isFinancialRecordChecklistTargetType` states the four record types positively rather than
 * subtracting Borrower — a future entity type must opt in deliberately.
 *
 * NOTE the loan-scoped complement is deliberately NOT merged in by consumers here (unlike
 * `Borrowers.js`): a `LoanApplication` snapshot's gap belongs to no single record, so stamping
 * it on every row would give all rows an identical count and defeat the point of a per-row
 * badge. Where such a gap DOES surface depends on the `fieldRef`'s shape:
 *  - a prefixed AREA ref (`subsection:financialInfo-assets`) — what the master completeness
 *    checklist authors today — resolves on `TableWrapper`'s `SectionPresenceWrapper`, so the
 *    grid's own header still flags it;
 *  - a bare FIELD path resolves on neither the section indicator (which matches area ids by
 *    equality) nor a row badge, and is visible only on the loan-navigation rail
 *    (`LoanNavigationTab` via `selectIncompleteLinkedNav`).
 *
 * @returns selector → `{ [recordId]: { [areaId]: { needed: true, count: number } } }`
 */
export const selectIncompleteLinkedNavByEntity = memoizeOne(() =>
  createSelector(
    selectLoanApplicationChecklistResultsList,
    selectCompletenessChecklistIdSet,
    (checklistResultsList, completenessChecklistIdSet) => {
      const byEntity = {};

      checklistResultsList.forEach(snapshot => {
        const checklistId = snapshot.target?.checklistId;
        const targetId = snapshot.target?.targetId;
        const targetType = snapshot.target?.targetType;
        const items = snapshot.status?.result?.items;

        if (
          !isFinancialRecordChecklistTargetType(targetType) ||
          !targetId ||
          !checklistId ||
          !items ||
          !completenessChecklistIdSet.has(checklistId)
        ) {
          return;
        }

        const map = byEntity[targetId] ?? {};
        accumulateNeededAreaIds(map, items);

        if (Object.keys(map).length) {
          byEntity[targetId] = map;
        }
      });

      return byEntity;
    },
  ),
);
